import {EventService} from "@app/core/tracking/application/event-service";
import {AdobeEventService} from "@app/core/tracking/infrastructure/adobe-event-service";
import {Injectable} from "@angular/core";
import {Event} from "@app/core/tracking/domain/event";

/**
 * Service implementation for sending events to one or more backends.
 */
@Injectable()
export class EventDataService implements EventService {
    /**
     * Map storing the last processed timestamp (in seconds) for each event type.
     */
    private lastEventTimestamps: Map<string, number> = new Map();

    constructor(
       private adobeEventDataService: AdobeEventService
    ) {}

    /**
     * Process an event, ensuring that if the same event is received within the same second, it is processed only once.
     * @param event - The event to be processed.
     */
    notify(event: Event): void {
        const nowInSeconds = Math.floor(Date.now() / 1000);
        const eventKey = this.generateEventKey(event);
        if (!this.isAllowedEvent(eventKey, nowInSeconds)) {
            return;
        }
        this.recordEvent(eventKey, nowInSeconds);
        this.adobeEventDataService.notify(event);
    }

    /**
     * Generates a unique key for the event based on its constructor.
     * @param event - The event for which to generate the key.
     * @returns The generated event key.
     */
    private generateEventKey(event: Event): string {
        return event.constructor.name;
    }

    /**
     * Determines if an event is allowed to be processed based on its last processed timestamp.
     * @param eventKey - The unique key of the event.
     * @param currentTimestamp - The current timestamp (in seconds).
     * @returns True if the event is allowed; false if it is considered a duplicate within one second.
     */
    private isAllowedEvent(eventKey: string, currentTimestamp: number): boolean {
        //The event is allowed if no previous timestamp exists or if more than one second has passed since the last event.
        const lastTimestamp = this.lastEventTimestamps.get(eventKey);
        if (lastTimestamp === undefined) {
            return true;
        }
        return (currentTimestamp - lastTimestamp) > 1;
    }


    /**
     * Records the event processing time by updating its timestamp.
     * @param eventKey - The unique key of the event.
     * @param timestamp - The current timestamp (in seconds).
     */
    private recordEvent(eventKey: string, timestamp: number): void {
        this.lastEventTimestamps.set(eventKey, timestamp);
    }
}